import { useMutation } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import type { paths } from "@src/packages/openapi/schema";
import type { RequestBody, ResponseBody } from "@src/packages/openapi/helpers";

export type NotificationDevice = ResponseBody<
  paths["/me/notification-devices"]["get"]
>["data"][number];

export type RegisterNotificationDevicePayload = RequestBody<
  paths["/me/notification-devices"]["post"]
>;

export function useRegisterNotificationDevice() {
  const { client } = useApiClient();

  return useMutation({
    mutationFn: async (
      payload: RegisterNotificationDevicePayload,
    ): Promise<NotificationDevice> => {
      const { data, error, response } = await client.POST(
        "/me/notification-devices",
        { body: payload },
      );
      if (error) {
        throwApiError(
          error,
          "We couldn't register this device for notifications.",
          "useRegisterNotificationDevice",
          response?.status,
        );
      }
      return data.data;
    },
  });
}

export function useRevokeNotificationDevice() {
  const { client } = useApiClient();

  return useMutation({
    mutationFn: async (pushToken: string): Promise<void> => {
      const { error, response } = await client.DELETE(
        "/me/notification-devices/current",
        { body: { pushToken } },
      );
      if (error) {
        throwApiError(
          error,
          "We couldn't unregister this device for notifications.",
          "useRevokeNotificationDevice",
          response?.status,
        );
      }
    },
  });
}
